import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  RiAddLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiBriefcaseLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiEditLine,
  RiEyeLine,
  RiFolderLine,
  RiImageLine,
  RiSearchLine,
  RiMapPinLine,
  RiMoneyDollarCircleLine,
  RiUserStarLine,
  RiGroupLine,
} from "react-icons/ri";
import { confirmAndDelete } from "../../utils/confirmAndDelete";
import JobPostFormModal from "../../components/AdminSideComponents/JobPost/JobPostFormModal";
import ModalWrapper from "../../components/common/ModalWrapper";
import CategoryFormModal from "../../components/AdminSideComponents/JobCategory/CategoryFormModal";
import ToggleSwitch from "../../components/AdminSideComponents/JobPost/ToggleSwitch";

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 animate-pulse">
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <div className="h-5 bg-gray-200 rounded w-40"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded"></div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-16">
    <Icon size={36} className="mx-auto text-gray-400 mb-3" />
    <h3 className="text-xl font-semibold text-gray-600 mb-2">{title}</h3>
    <p className="text-gray-500">{description}</p>
  </div>
);

const JobPosts = () => {
  const API = process.env.REACT_APP_API_URL;
  const searchTimeout = useRef(null);

  const [activeTab, setActiveTab] = useState("categories");
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [jobPosts, setJobPosts] = useState([]);

  const [postPagination, setPostPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 6,
  });

  const [postPage, setPostPage] = useState(1);

  const [postFilters, setPostFilters] = useState({
    category: "",
    roleName: "",
    isActive: "",
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [categoryCreateOpen, setCategoryCreateOpen] = useState(false);
  const [categoryEditOpen, setCategoryEditOpen] = useState(false);
  const [jobPostCreateOpen, setJobPostCreateOpen] = useState(false);
  const [jobPostEditOpen, setJobPostEditOpen] = useState(false);

  const limit = 6;

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/job-category/get-all?page=1&limit=1000`
      );
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobPosts = async (page = 1, filters = postFilters) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page,
        limit,
      });

      if (filters.category) params.append("category", filters.category);
      if (filters.roleName) params.append("roleName", filters.roleName);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);

      const res = await axios.get(
        `${API}/job-post/get-all?${params.toString()}`
      );

      setJobPosts(res.data.jobPosts || []);
      setPostPagination(
        res.data.pagination || {
          total: 0,
          currentPage: 1,
          totalPages: 1,
          limit,
        }
      );
    } catch (error) {
      console.error("Error fetching job posts:", error);
      toast.error("Failed to fetch job posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (activeTab === "posts") {
      fetchJobPosts(postPage);
    }
  }, [activeTab, postPage]);

  const triggerPostSearch = (updatedFilters) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPostPage(1);
      fetchJobPosts(1, updatedFilters);
    }, 500);
  };

  const handlePostFilterChange = (key, value) => {
    const updated = { ...postFilters, [key]: value };
    setPostFilters(updated);
    triggerPostSearch(updated);
  };

  const openViewModal = (item) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedItem(null);
    setIsViewModalOpen(false);
  };

  const deleteCategory = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${API}/job-category/delete`,
    });

    if (isConfirmed) {
      await fetchCategories();
      Swal.fire("Deleted!", "Category deleted successfully.", "success");
    }
  };

  const deleteJobPost = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${API}/job-post/delete`,
    });

    if (isConfirmed) {
      await fetchJobPosts(postPage);
      Swal.fire("Deleted!", "Job post deleted successfully.", "success");
    }
  };

  const toggleJobPostStatus = async (id, currentStatus) => {
    try {
      setTogglingId(id);
      const res = await axios.put(`${API}/job-post/update/${id}`, {
        isActive: !currentStatus,
      });

      if (res.data.success) {
        setJobPosts((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, isActive: !currentStatus } : item
          )
        );

        if (selectedItem?._id === id) {
          setSelectedItem((prev) => ({ ...prev, isActive: !currentStatus }));
        }

        toast.success("Status updated");
      }
    } catch (error) {
      console.error("Error updating job post status:", error);
      toast.error("Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const getVisiblePages = (totalPages, currentPage) => {
    if (totalPages <= 4)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2)
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage, "...", totalPages];
  };

  const renderPagination = (pagination, currentPage, setPage) => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
        <button
          onClick={() => setPage(1)}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg text-sm font-medium ${
            currentPage === 1
              ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
              : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
          }`}
        >
          First
        </button>

        <button
          onClick={() => currentPage > 1 && setPage(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg ${
            currentPage === 1
              ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
              : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
          }`}
        >
          <RiArrowLeftSLine size={20} />
        </button>

        {getVisiblePages(pagination.totalPages, currentPage).map(
          (page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="px-2 py-1 text-gray-400 font-medium"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => setPage(page)}
                className={`px-3 py-1 rounded-lg text-sm ${
                  currentPage === page
                    ? "bg-primaryBtnColor text-white"
                    : "bg-primaryTextColor text-gray-300 hover:bg-secondaryTextColor"
                }`}
              >
                {page}
              </button>
            )
        )}

        <button
          onClick={() =>
            currentPage < pagination.totalPages && setPage(currentPage + 1)
          }
          disabled={currentPage === pagination.totalPages}
          className={`p-2 rounded-lg ${
            currentPage === pagination.totalPages
              ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
              : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
          }`}
        >
          <RiArrowRightSLine size={20} />
        </button>

        <button
          onClick={() => setPage(pagination.totalPages)}
          disabled={currentPage === pagination.totalPages}
          className={`px-3 py-2 rounded-lg text-sm font-medium ${
            currentPage === pagination.totalPages
              ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
              : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
          }`}
        >
          Last
        </button>
      </div>
    );
  };

  const currentEditCategory = useMemo(
    () =>
      selectedItem && selectedItem.type === "category" ? selectedItem : null,
    [selectedItem]
  );

  const currentEditPost = useMemo(
    () => (selectedItem && selectedItem.type === "post" ? selectedItem : null),
    [selectedItem]
  );

  return (
    <div className="min-h-screen bg-transparent p-4 md:p-6">
      <div>
        <div className="flex flex-col gap-4 mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            Job Posts
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                className={`px-5 py-2 rounded-lg font-semibold transition ${
                  activeTab === "categories"
                    ? "bg-primaryBtnColor text-white"
                    : "bg-white text-primaryTextColor border"
                }`}
                onClick={() => setActiveTab("categories")}
              >
                Job Categories
              </button>

              <button
                className={`px-5 py-2 rounded-lg font-semibold transition ${
                  activeTab === "posts"
                    ? "bg-primaryBtnColor text-white"
                    : "bg-white text-primaryTextColor border"
                }`}
                onClick={() => setActiveTab("posts")}
              >
                Job Posts
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {activeTab === "categories" ? (
                <button
                  onClick={() => setCategoryCreateOpen(true)}
                  className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2 px-4 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <RiAddLine size={18} />
                  Create Category
                </button>
              ) : (
                <button
                  onClick={() => setJobPostCreateOpen(true)}
                  className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2 px-4 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <RiAddLine size={18} />
                  Create Job Post
                </button>
              )}
            </div>
          </div>
        </div>

        {activeTab === "categories" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
              {loading
                ? Array.from({ length: limit }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                : categories.map((category) => (
                    <div
                      key={category._id}
                      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative border border-gray-100"
                    >
                      {/* Top accent bar */}
                      <div className="h-1.5 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor"></div>

                      {/* Action buttons - redesigned as subtle chips */}
                      <div className="absolute top-3 right-3 flex space-x-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          onClick={() =>
                            openViewModal({ ...category, type: "category" })
                          }
                          className="p-2 bg-white/90 backdrop-blur-sm rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-all duration-200 shadow-sm hover:shadow-md"
                          title="View details"
                        >
                          <RiEyeLine size={14} />
                        </button>

                        <button
                          onClick={() => {
                            setSelectedItem({ ...category, type: "category" });
                            setCategoryEditOpen(true);
                          }}
                          className="p-2 bg-white/90 backdrop-blur-sm rounded-lg text-blue-500 hover:bg-blue-500 hover:text-white transition-all duration-200 shadow-sm hover:shadow-md"
                          title="Edit category"
                        >
                          <RiEditLine size={14} />
                        </button>

                        <button
                          onClick={() => deleteCategory(category._id)}
                          className="p-2 bg-white/90 backdrop-blur-sm rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-all duration-200 shadow-sm hover:shadow-md"
                          title="Delete category"
                        >
                          <RiDeleteBinLine size={14} />
                        </button>
                      </div>

                      {/* Card content */}
                      <div className="p-6 pt-5">
                        {/* Icon and title section */}
                        <div className="flex items-start space-x-3 mb-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-gradient-to-br from-primaryBtnColor to-primaryBtnHoverColor rounded-xl flex items-center justify-center text-white shadow-md group-hover:shadow-lg transition-all duration-300">
                              <RiFolderLine size={22} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-primaryTextColor group-hover:text-primaryBtnHoverColor transition-colors duration-200 truncate">
                              {category.name}
                            </h3>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="pl-0">
                          <p className="text-secondaryTextColor text-sm leading-relaxed line-clamp-3 min-h-[4.5rem]">
                            {category.description || (
                              <span className="italic opacity-60">
                                No description provided
                              </span>
                            )}
                          </p>
                        </div>

                        {/* Optional: subtle meta information */}
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                          <div className="flex items-center space-x-1 text-xs text-secondaryTextColor/60">
                            <RiFolderLine size={12} />
                            <span>Category</span>
                          </div>
                          <div className="text-xs text-primaryBtnColor opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            Click to manage →
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>

            {!loading && categories.length === 0 && (
              <EmptyState
                icon={RiFolderLine}
                title="No categories found"
                description="Categories will appear here."
              />
            )}
          </>
        )}

        {activeTab === "posts" && (
          <>
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 border">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="relative">
                  <RiSearchLine
                    className="absolute left-3 top-3 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="Search role name..."
                    value={postFilters.roleName}
                    onChange={(e) =>
                      handlePostFilterChange("roleName", e.target.value)
                    }
                    className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select
                    value={postFilters.category}
                    onChange={(e) =>
                      handlePostFilterChange("category", e.target.value)
                    }
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={postFilters.isActive}
                    onChange={(e) =>
                      handlePostFilterChange("isActive", e.target.value)
                    }
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* <div className="flex justify-end mb-4">
              <div className="bg-primaryBtnColor text-white px-4 py-2 rounded-lg font-semibold">
                Total: {postPagination.total} job posts
              </div>
            </div> */}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
              {loading
                ? Array.from({ length: limit }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                : jobPosts.map((post) => (
                    <div
                      key={post._id}
                      className="bg-white rounded-2xl overflow-hidden shadow-xl border border-primaryTextColor transition-transform hover:scale-[1.02] relative"
                    >
                      <div className="absolute top-4 right-4 flex space-x-2 z-10">
                        <button
                          onClick={() =>
                            openViewModal({ ...post, type: "post" })
                          }
                          className="p-2 bg-white rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors shadow-md"
                        >
                          <RiEyeLine size={16} />
                        </button>

                        <button
                          onClick={() => {
                            setSelectedItem({ ...post, type: "post" });
                            setJobPostEditOpen(true);
                          }}
                          className="p-2 bg-white rounded-lg text-blue-500 hover:bg-blue-500 hover:text-white transition-colors shadow-md"
                        >
                          <RiEditLine size={16} />
                        </button>

                        <button
                          onClick={() => deleteJobPost(post._id)}
                          className="p-2 bg-white rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-md"
                        >
                          <RiDeleteBinLine size={16} />
                        </button>
                      </div>

                      <div className="p-6">
                        {/* Header */}
                        <div className="flex items-start mb-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-full flex items-center justify-center text-white font-bold mr-4 shrink-0">
                            <RiBriefcaseLine size={22} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-primaryTextColor line-clamp-1">
                              {post.roleName}
                            </h3>
                            <p className="text-sm text-secondaryTextColor truncate">
                              {post.category?.name || "N/A"}
                            </p>
                          </div>
                        </div>

                        {/* Meta Info */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-50 rounded-xl px-3 py-2">
                            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">
                              Location
                            </p>
                            <p className="text-sm font-semibold text-primaryTextColor truncate">
                              {post.location || "N/A"}
                            </p>
                          </div>

                          <div className="bg-gray-50 rounded-xl px-3 py-2">
                            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">
                              Salary
                            </p>
                            <p className="text-sm font-semibold text-primaryTextColor truncate">
                              {post.salary || "N/A"}
                            </p>
                          </div>

                          <div className="bg-gray-50 rounded-xl px-3 py-2">
                            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">
                              Vacancies
                            </p>
                            <p className="text-sm font-semibold text-primaryTextColor">
                              {post.vacancies || "N/A"}
                            </p>
                          </div>

                          <div className="bg-gray-50 rounded-xl px-3 py-2">
                            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">
                              Experience
                            </p>
                            <p className="text-sm font-semibold text-primaryTextColor truncate">
                              {post.experience || "N/A"}
                            </p>
                          </div>
                        </div>

                        {/* Description */}
                        {/* <div
                          className="text-secondaryTextColor min-h-16 line-clamp-3 text-sm leading-relaxed mb-4 quill-content"
                          dangerouslySetInnerHTML={{
                            __html:
                              post.description ||
                              "<p>No description provided</p>",
                          }}
                        /> */}

                        {/* Footer */}
                        <div className="flex justify-between items-center gap-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              post.isActive
                                ? "bg-green-100 text-green-600"
                                : "bg-red-100 text-red-600"
                            }`}
                          >
                            {post.isActive ? "Active" : "Inactive"}
                          </span>

                          <ToggleSwitch
                            checked={post.isActive}
                            disabled={togglingId === post._id}
                            onChange={() =>
                              toggleJobPostStatus(post._id, post.isActive)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
            </div>

            {!loading && jobPosts.length === 0 && (
              <EmptyState
                icon={RiBriefcaseLine}
                title="No job posts found"
                description="Job posts will appear here."
              />
            )}

            {renderPagination(postPagination, postPage, setPostPage)}
          </>
        )}

        {/* VIEW MODAL */}
        {isViewModalOpen && selectedItem && (
          <ModalWrapper
            title={
              selectedItem.type === "category"
                ? "Category Details"
                : "Job Post Details"
            }
            onClose={closeViewModal}
            maxWidth="max-w-3xl"
          >
            {selectedItem.type === "category" ? (
              <>
                <div className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl p-6 mb-6 text-white">
                  <div className="flex items-center">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white mr-4">
                      <RiFolderLine size={28} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">
                        {selectedItem.name}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <div className="flex items-center mb-2">
                    <RiImageLine
                      className="text-primaryBtnColor mr-2"
                      size={18}
                    />
                    <span className="font-semibold text-gray-700">Image</span>
                  </div>
                  {selectedItem.image ? (
                    <img
                      src={selectedItem.image}
                      alt={selectedItem.name}
                      className="w-full h-56 object-cover rounded-xl"
                    />
                  ) : (
                    <p className="text-primaryTextColor">No image uploaded</p>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Description
                  </h4>
                  <p className="text-primaryTextColor leading-relaxed">
                    {selectedItem.description || "No description provided"}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl p-6 mb-6 text-white">
                  <div className="flex items-center">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white mr-4">
                      <RiBriefcaseLine size={28} />
                    </div>

                    <div>
                      <h3 className="text-2xl font-bold">
                        {selectedItem.roleName}
                      </h3>
                      <p className="text-sm opacity-90">
                        {selectedItem.category?.name || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <RiFolderLine
                        className="text-primaryBtnColor mr-2"
                        size={18}
                      />
                      <span className="font-semibold text-gray-700">
                        Category
                      </span>
                    </div>
                    <p className="text-primaryTextColor">
                      {selectedItem.category?.name || "N/A"}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <RiBriefcaseLine
                        className="text-primaryBtnColor mr-2"
                        size={18}
                      />
                      <span className="font-semibold text-gray-700">
                        Status
                      </span>
                    </div>
                    <p
                      className={`font-medium ${
                        selectedItem.isActive
                          ? "text-green-600"
                          : "text-red-500"
                      }`}
                    >
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <RiMapPinLine
                        className="text-primaryBtnColor mr-2"
                        size={18}
                      />
                      <span className="font-semibold text-gray-700">
                        Location
                      </span>
                    </div>
                    <p className="text-primaryTextColor">
                      {selectedItem.location || "Not specified"}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <RiMoneyDollarCircleLine
                        className="text-primaryBtnColor mr-2"
                        size={18}
                      />
                      <span className="font-semibold text-gray-700">
                        Salary
                      </span>
                    </div>
                    <p className="text-primaryTextColor">
                      {selectedItem.salary || "Not specified"}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <RiUserStarLine
                        className="text-primaryBtnColor mr-2"
                        size={18}
                      />
                      <span className="font-semibold text-gray-700">
                        Experience
                      </span>
                    </div>
                    <p className="text-primaryTextColor">
                      {selectedItem.experience || "Not specified"}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <RiGroupLine
                        className="text-primaryBtnColor mr-2"
                        size={18}
                      />
                      <span className="font-semibold text-gray-700">
                        Vacancies
                      </span>
                    </div>
                    <p className="text-primaryTextColor">
                      {selectedItem.vacancies || "Not specified"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Description
                  </h4>

                  <div
                    className="text-secondaryTextColor text-sm leading-relaxed quill-content"
                    dangerouslySetInnerHTML={{
                      __html:
                        selectedItem.description ||
                        "<p>No description provided</p>",
                    }}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={closeViewModal}
                className="bg-gray-100 text-primaryTextColor py-2 px-6 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </ModalWrapper>
        )}

        {/* FORM MODALS */}
        <CategoryFormModal
          mode="create"
          API={API}
          open={categoryCreateOpen}
          onClose={() => setCategoryCreateOpen(false)}
          onSuccess={fetchCategories}
        />

        <CategoryFormModal
          mode="edit"
          API={API}
          open={categoryEditOpen}
          onClose={() => {
            setCategoryEditOpen(false);
            setSelectedItem(null);
          }}
          onSuccess={fetchCategories}
          initialData={currentEditCategory}
        />

        <JobPostFormModal
          mode="create"
          API={API}
          open={jobPostCreateOpen}
          onClose={() => setJobPostCreateOpen(false)}
          onSuccess={() => fetchJobPosts(postPage)}
          categories={categories}
        />

        <JobPostFormModal
          mode="edit"
          API={API}
          open={jobPostEditOpen}
          onClose={() => {
            setJobPostEditOpen(false);
            setSelectedItem(null);
          }}
          onSuccess={() => fetchJobPosts(postPage)}
          categories={categories}
          initialData={currentEditPost}
        />
      </div>
    </div>
  );
};

export default JobPosts;
